'use strict';

/* eslint-disable @typescript-eslint/no-var-requires */
const { nextflowProcessTimelineState } = require('./nextflow-process-status');
const { resolveNextflowArtifactPath } = require('./nextflow-artifact-path');

const PROCESS_DATA_TYPES = Object.freeze({
  FLOAT: 'float',
  INTEGER: 'integer',
  STRING: 'string',
  FILE: 'file',
  DIRECTORY: 'directory',
});

const MERMAID_WEB_FORMAT = 'mermaid';

/**
 * Map persisted `progressResults.processes[]` onto the rows the run panel
 * renders.
 *
 * Extracted from NextflowTask.vue so the mapping can be tested without a
 * component harness. Kept pure: the mermaid diagram is returned rather than
 * assigned to a ref. `mermaidDiagram` is `undefined` when no mermaid entry was
 * seen at all, which the caller must treat as "leave the current diagram
 * alone" — distinct from an entry whose value was nullish, which yields `null`.
 *
 * Metrics preserve the original mixed
 * casing: routing reads camelCase (`dataType`, `webFormat`) while the emitted
 * metric row reads snake_case (`data_type`, `file_type`, ...). See the tests,
 * which pin that quirk rather than quietly correcting it.
 *
 * @param {any} processes persisted processes array, or any nullish value
 * File rows resolve against the task's same-bucket output root for every
 * preview, directory and download consumer. Invalid locations fail closed.
 *
 * @param {{ exposeHiddenResults?: boolean, artifactLocation?: object }} [options]
 * @returns {{ processes: any[], mermaidDiagram?: string | null, artifactPathError: boolean }}
 */
function buildProgressData(processes, options) {
  const exposeHiddenResults = Boolean(options && options.exposeHiddenResults);
  const inProcesses = processes || [];
  const outProcesses = [];
  let mermaidDiagram;
  let artifactPathError = false;

  for (const proc of inProcesses) {
    const pResults = [];
    const pFiles = [];

    for (const item of proc.results ?? []) {
      if (item.public === true || exposeHiddenResults) {
        if (item.webFormat === MERMAID_WEB_FORMAT) {
          mermaidDiagram = item.value ?? null;
        } else if (
          item.dataType === PROCESS_DATA_TYPES.DIRECTORY ||
          item.dataType === PROCESS_DATA_TYPES.FILE
        ) {
          const path = resolveNextflowArtifactPath(
            item.value,
            options?.artifactLocation
          );
          if (path === null) {
            artifactPathError = true;
            continue;
          }
          pFiles.push({
            name: item.label ?? '',
            path,
            type: item.fileCategory ?? '',
            fileType: item.fileType ?? '',
            description: item.description ?? '',
          });
        } else {
          pResults.push({
            key: item.key ?? '',
            label: item.label ?? '',
            value: item.value ?? '',
            description: item.description ?? '',
            public: item.public ?? false,
            dataType: item.data_type ?? '',
            fileType: item.file_type ?? undefined,
            fileCategory: item.file_category ?? undefined,
            webFormat: item.web_format ?? undefined,
          });
        }
      }
    }

    outProcesses.push({
      scope: proc.scope ?? '',
      name: proc.process_name ?? '',
      status: proc.status || '',
      state: nextflowProcessTimelineState(proc.status),
      sampleId: proc.sample_id ?? undefined,
      results: pResults,
      files: pFiles,
    });
  }

  return { processes: outProcesses, mermaidDiagram, artifactPathError };
}

module.exports = {
  PROCESS_DATA_TYPES,
  buildProgressData,
};
